import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { 
  Globe, 
  Shield, 
  CheckCircle, 
  AlertTriangle,
  Clock,
  Trash2,
  RefreshCw,
  Plus,
  Search,
  ExternalLink,
  Server,
  Lock
} from 'lucide-react';
import { toast } from 'sonner';

const SubdomainManager = () => {
  const [subdomains, setSubdomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [creating, setCreating] = useState(false);
  const [newSubdomain, setNewSubdomain] = useState({
    subdomain: '',
    departmentId: ''
  });

  const backendUrl = import.meta.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchSubdomains();
    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchSubdomains, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchSubdomains = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${backendUrl}/api/master/subdomains`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSubdomains(data);
      } else {
        console.error('Failed to fetch subdomains');
        toast.error('Failed to fetch subdomains');
      }
    } catch (error) {
      console.error('Error fetching subdomains:', error);
      toast.error('Error fetching subdomains');
    } finally {
      setLoading(false);
    }
  };

  const validateSubdomain = async (subdomain) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${backendUrl}/api/master/validate-subdomain`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ subdomain })
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      }
      return { valid: false, reason: 'Validation failed' };
    } catch (error) {
      return { valid: false, reason: 'Network error' };
    }
  };

  const createSubdomain = async () => {
    if (!newSubdomain.subdomain.trim() || !newSubdomain.departmentId.trim()) {
      toast.error('Please fill in both subdomain and department ID');
      return;
    }

    setCreating(true);
    try {
      // Validate first
      const validation = await validateSubdomain(newSubdomain.subdomain);
      if (!validation.valid) {
        toast.error(`Invalid subdomain: ${validation.reason}`);
        return;
      }

      const token = localStorage.getItem('access_token');
      const response = await fetch(`${backendUrl}/api/master/create-subdomain`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newSubdomain)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Subdomain created successfully!');
        setNewSubdomain({ subdomain: '', departmentId: '' });
        fetchSubdomains(); // Refresh list
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create subdomain');
      }
    } catch (error) {
      console.error('Error creating subdomain:', error);
      toast.error('Failed to create subdomain');
    } finally {
      setCreating(false);
    }
  };

  const deleteSubdomain = async (subdomain, departmentId) => {
    if (!confirm(`Are you sure you want to delete subdomain "${subdomain}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${backendUrl}/api/master/delete-subdomain`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ subdomain, department_id: departmentId })
      });

      if (response.ok) {
        toast.success('Subdomain deleted successfully');
        fetchSubdomains(); // Refresh list
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete subdomain');
      }
    } catch (error) {
      console.error('Error deleting subdomain:', error);
      toast.error('Failed to delete subdomain');
    }
  };

  const refreshSubdomainStatus = async (subdomain) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${backendUrl}/api/master/subdomain-status/${subdomain}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toast.success('Status refreshed');
        fetchSubdomains(); // Refresh full list
      } else {
        toast.error('Failed to refresh status');
      }
    } catch (error) {
      toast.error('Error refreshing status');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800">Active</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-800">Failed</Badge>;
      case 'deleted':
        return <Badge variant="outline" className="text-gray-500">Deleted</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getSSLBadge = (sslStatus) => {
    switch (sslStatus) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800 flex items-center gap-1">
          <Lock className="h-3 w-3" />
          SSL Active
        </Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800 flex items-center gap-1">
          <Clock className="h-3 w-3" />
          SSL Pending
        </Badge>;
      case 'expired':
        return <Badge className="bg-red-100 text-red-800 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" />
          SSL Expired
        </Badge>;
      default:
        return <Badge variant="outline">SSL Unknown</Badge>;
    }
  };

  const filteredSubdomains = subdomains.filter(sub => 
    sub.subdomain.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sub.department_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">Loading subdomain data...</span>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Subdomain Management</h1>
          <p className="text-gray-600 mt-1">Manage DNS subdomains for all departments</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={fetchSubdomains}
            variant="outline"
            size="sm"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Create New Subdomain */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Create New Subdomain
          </CardTitle>
          <CardDescription>
            Manually create a subdomain for a department
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Subdomain</label>
              <div className="flex items-center">
                <Input
                  value={newSubdomain.subdomain}
                  onChange={(e) => setNewSubdomain({...newSubdomain, subdomain: e.target.value.toLowerCase()})}
                  placeholder="shakerpd"
                  className="rounded-r-none"
                />
                <span className="px-3 py-2 bg-gray-100 border border-l-0 rounded-r-md text-sm text-gray-600">
                  .reqtrak.com
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Department ID</label>
              <Input
                value={newSubdomain.departmentId}
                onChange={(e) => setNewSubdomain({...newSubdomain, departmentId: e.target.value})}
                placeholder="Department UUID"
              />
            </div>
          </div>

          <Button 
            onClick={createSubdomain}
            disabled={creating}
            className="w-full"
          >
            {creating ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                Create Subdomain
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-gray-400" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search subdomains or departments..."
              className="flex-1"
            />
          </div>
        </CardContent>
      </Card>

      {/* Subdomains List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            All Subdomains ({filteredSubdomains.length})
          </CardTitle>
          <CardDescription>
            DNS records and SSL status for all department subdomains
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredSubdomains.map((sub, index) => (
              <div key={index} className="border rounded-lg p-4 bg-gray-50">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold">{sub.subdomain}.reqtrak.com</h3>
                      <a 
                        href={`https://${sub.full_domain}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </div>
                    
                    <p className="text-gray-600 mb-2">
                      <strong>Department:</strong> {sub.department_name}
                    </p>
                    
                    <p className="text-sm text-gray-500">
                      Created: {new Date(sub.created_at).toLocaleString()}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    {getStatusBadge(sub.status)}
                    {getSSLBadge(sub.ssl_status)}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                  <div className="flex items-center gap-2">
                    <Server className="h-4 w-4 text-gray-400" />
                    <span className="text-sm">
                      DNS: {sub.dns_propagated ? (
                        <span className="text-green-600">Propagated</span>
                      ) : (
                        <span className="text-yellow-600">Pending</span>
                      )}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-gray-400" />
                    <span className="text-sm">
                      SSL: {sub.ssl_verified ? (
                        <span className="text-green-600">Verified</span>
                      ) : (
                        <span className="text-yellow-600">Pending</span>
                      )}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-500">
                      Updated: {new Date(sub.updated_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between border-t pt-3">
                  <div className="text-sm text-gray-600">
                    <strong>Department ID:</strong> {sub.department_id}
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      onClick={() => refreshSubdomainStatus(sub.subdomain)}
                      variant="outline"
                      size="sm"
                    >
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Refresh
                    </Button>
                    
                    <Button
                      onClick={() => deleteSubdomain(sub.subdomain, sub.department_id)}
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredSubdomains.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Globe className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No subdomains found.</p>
              {searchTerm && (
                <p className="text-sm">Try adjusting your search criteria.</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SubdomainManager;